import { registrarEvento } from "../core/auditLog.js";
import { repararTildes } from "../core/tildes.js";
import { ajustarInventario, crearAsiento, generarAsientoNotaCredito } from "../core/accounting.js";
import { congelarRegimenItems, desgloseIVA, renglonesIVA, etiquetaRegimen, regimenDeLinea } from "../core/iva.js";
import { saveData, siguienteNumeroDoc } from "../core/cloudSync.js";
import { genId, today, todayCO } from "../core/utils.js";
import { itemDataico } from "../core/dataicoPayload.js";
import { exportPDFNotaCredito } from "../export/pdf.js";
import { getDataicoConfig, saveDataicoConfig } from "./dataico.js";
import { supa } from "../core/supabaseClient.js";

import { C, ESTADOS, ESTADO_COLOR } from "../core/config.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, fmtCOP, fmtDate, inputStyle, parseNumber } from "../core/utils.js";
import { render } from "../ui/app.js";
import { getSaldoFactura, getTotalNC } from "./facturas.js";

// Motivos de la nota crédito con el código (reason) que espera Dataico. Se
// limitan a los confirmados por la API para no arriesgar rechazos; el resto
// de casos entra por "Otro motivo".
const MOTIVOS_NC = [
  { code: "ANULACION", label: "Anulación de la factura" },
  { code: "DEVOLUCION", label: "Devolución de mercancía / no aceptación" },
  { code: "OTROS", label: "Otro motivo (ajuste, descuento, corrección…)" },
];

// ¿La factura fue emitida DE VERDAD por Dataico? Solo esas se pueden anular
// electrónicamente ante la DIAN referenciando su documento.
function facturaEsElectronicaReal(f) {
  return !!(f && f._dataico && f._dataico.real && (f._dataico.uuid || f._dataico.cufe));
}

function fechaDataico(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

export function renderNotaCreditoModal() {
  const nc = state.editingNC;
  if (!nc) return;
  const f = (state.data.facturas||[]).find(x=>x.id===nc.facturaId);
  if (!f) { state.editingNC = null; return; }
  const cli = state.data.clientes.find(c=>c.id===f.cliente_id) || {};

  const yaCreditado = getTotalNC(f.id);
  const saldoActual = getSaldoFactura(f);

  const subtotalNC = nc.items.reduce((s,it) => s + (parseNumber(it.cantidad)*parseNumber(it.precio_unitario)), 0);
  const ivaNC = desgloseIVA(nc.items.map(it => ({
    ...it, total: parseNumber(it.cantidad) * parseNumber(it.precio_unitario)
  }))).iva;
  const totalNC = subtotalNC + ivaNC;

  const esReal = facturaEsElectronicaReal(f);
  const cfg = getDataicoConfig() || {};

  const itemsRows = nc.items.map((it,i) => `
    <tr style="border-top:1px solid ${C.border};">
      <td style="padding:6px 8px;">${i+1}</td>
      <td style="padding:6px 8px;font-weight:600;">${escapeHTML(it.articulo)}</td>
      <td style="padding:6px 8px;color:${C.muted};font-size:10.5px;">${escapeHTML(it.descripcion||"")}</td>
      <td style="padding:6px 8px;text-align:right;color:${C.muted};">${it.cantidad_factura}</td>
      <td style="padding:6px 8px;text-align:right;">${fmtCOP(it.precio_unitario)}</td>
      <td style="padding:6px 8px;width:100px;">
        <input data-nc-item="${i}" type="number" min="0" max="${it.cantidad_factura}" inputmode="decimal" value="${it.cantidad}" style="${inputStyle}text-align:right;padding:5px 7px;"/>
      </td>
      <td style="padding:6px 8px;text-align:right;font-weight:700;">${fmtCOP(parseNumber(it.cantidad)*parseNumber(it.precio_unitario))}</td>
    </tr>
  `).join("");

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:760px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;background:#f8fafc;">
          <div>
            <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">🧾 Generar Nota Crédito</h3>
            <div style="font-size:11.5px;color:${C.muted};margin-top:2px;">Sobre ${escapeHTML(f.numero)} — ${escapeHTML(cli.nombre||"")}</div>
          </div>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;background:#f8fafc;border-radius:6px;padding:9px;margin-bottom:9px;text-align:center;">
            <div>
              <div style="font-size:10px;color:${C.muted};font-weight:700;">TOTAL FACTURA</div>
              <div style="font-size:12px;font-weight:800;color:${C.text};">${fmtCOP(f.total)}</div>
            </div>
            <div>
              <div style="font-size:10px;color:${C.muted};font-weight:700;">YA ACREDITADO</div>
              <div style="font-size:12px;font-weight:800;color:#dc2626;">${fmtCOP(yaCreditado)}</div>
            </div>
            <div>
              <div style="font-size:10px;color:${C.muted};font-weight:700;">SALDO ACTUAL</div>
              <div style="font-size:12px;font-weight:800;color:#0E7490;">${fmtCOP(saldoActual)}</div>
            </div>
          </div>

          ${esReal ? `
            <div style="background:#ecfdf5;border:1px solid #86efac;border-radius:6px;padding:9px;margin-bottom:10px;font-size:11.5px;">
              <div style="font-weight:700;color:#065f46;">🇨🇴 Se emitirá electrónicamente ante la DIAN</div>
              <div style="color:#047857;margin-top:2px;">La factura ${escapeHTML(f.numero)} es electrónica real; esta nota crédito se transmite a Dataico → DIAN y le llega al cliente con CUDE oficial.</div>
            </div>
          ` : `
            <div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:6px;padding:9px;margin-bottom:10px;font-size:11.5px;">
              <div style="font-weight:700;color:#92400e;">📄 Nota crédito local (sin DIAN)</div>
              <div style="color:#78350f;margin-top:2px;">La factura ${escapeHTML(f.numero)} no es electrónica, así que esta nota crédito queda solo en Aura, sin validez fiscal.</div>
            </div>
          `}

          <div style="margin-bottom:12px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Motivo de la nota crédito</label>
            <select data-nc-motivo style="${inputStyle}cursor:pointer;">
              <option value="">— Selecciona un motivo —</option>
              ${MOTIVOS_NC.map(m => `<option value="${m.code}" ${nc.reason===m.code?"selected":""}>${escapeHTML(m.label)}</option>`).join("")}
            </select>
          </div>

          ${esReal ? `
            <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:10px;">
              <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:6px;">NUMERACIÓN DE NOTA CRÉDITO (la de tu resolución de NC en Dataico)</div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
                <div>
                  <label style="font-size:10px;color:${C.muted};font-weight:600;">Prefijo</label>
                  <input id="nc-prefijo" value="${escapeHTML(cfg.nc_prefijo || "NC")}" placeholder="Ej: NC" style="${inputStyle}"/>
                </div>
                <div>
                  <label style="font-size:10px;color:${C.muted};font-weight:600;">N° de esta nota crédito</label>
                  <input id="nc-numero" type="number" min="1" value="${cfg.nc_proximo_numero != null ? escapeHTML(String(cfg.nc_proximo_numero)) : ""}" placeholder="Consecutivo" style="${inputStyle}"/>
                </div>
              </div>
              <div style="font-size:10px;color:${C.muted};margin-top:5px;">💡 Debe continuar el consecutivo real de tu resolución de NC en Dataico. Después de la primera, Aura lo lleva solo.</div>
            </div>
          ` : ""}

          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Ítems a acreditar (cantidad)</label>
            <button data-action="nc-anular-todo" style="${btnStyle("#dc2626",null,true)}">Anular factura completa</button>
          </div>
          <div style="overflow-x:auto;">
            <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
              <thead>
                <tr style="background:#fef2f2;">
                  <th style="padding:6px 8px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">#</th>
                  <th style="padding:6px 8px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Artículo</th>
                  <th style="padding:6px 8px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Descripción</th>
                  <th style="padding:6px 8px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">Cant. Fact.</th>
                  <th style="padding:6px 8px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">P.Unit</th>
                  <th style="padding:6px 8px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">Cant. a anular</th>
                  <th style="padding:6px 8px;text-align:right;font-size:10.5px;font-weight:700;color:${C.text};">Total NC</th>
                </tr>
              </thead>
              <tbody>${itemsRows}</tbody>
            </table>
          </div>

          <div style="text-align:right;margin-top:12px;padding-top:10px;border-top:2px solid #dc2626;">
            <div style="font-size:11.5px;color:${C.muted};">Subtotal: <strong>${fmtCOP(subtotalNC)}</strong></div>
            ${renglonesIVA(nc.items.map(it => ({ ...it, total: parseNumber(it.cantidad) * parseNumber(it.precio_unitario) })), ivaNC).map(r => `<div style="font-size:11.5px;color:${C.muted};">${escapeHTML(r.lbl)}: <strong>${fmtCOP(r.valor)}</strong></div>`).join("")}
            <div style="font-size:17px;font-weight:800;color:#dc2626;margin-top:4px;">TOTAL NOTA CRÉDITO: ${fmtCOP(totalNC)}</div>
          </div>

          ${totalNC > saldoActual + 0.01 ? `
            <div style="margin-top:10px;padding:10px;background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;font-size:10.5px;color:#78350f;">
              ⚠️ El valor de la nota crédito (${fmtCOP(totalNC)}) supera el saldo actual de la factura (${fmtCOP(saldoActual)}).
            </div>
          ` : ""}

          <div style="display:flex;gap:10px;justify-content:flex-end;padding-top:14px;border-top:1px solid ${C.border};margin-top:14px;">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cancelar</button>
            <button data-action="confirmar-nota-credito" style="${btnStyle("#dc2626")}">${esReal ? "⚡ Emitir Nota Crédito electrónica" : "🧾 Generar Nota Crédito local"}</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

export function renderNotasCredito() {
  const ncs = state.data.notas_credito || [];
  const totalNC = ncs.filter(n=>n.estado!=="anulada").reduce((s,n)=>s+(Number(n.total)||0),0);

  const ncFiltroEstado = state.ncFiltroEstado || "";
  const ncFiltroCliente = state.ncFiltroCliente || "";
  const ncFiltroDesde = state.ncFiltroDesde || "";
  const ncFiltroHasta = state.ncFiltroHasta || "";
  const clientesNC = state.data.clientes || [];

  let docsNC = [...ncs];
  if (ncFiltroEstado) docsNC = docsNC.filter(n => n.estado === ncFiltroEstado);
  if (ncFiltroCliente) docsNC = docsNC.filter(n => n.cliente_id === ncFiltroCliente);
  if (ncFiltroDesde) docsNC = docsNC.filter(n => n.fecha >= ncFiltroDesde);
  if (ncFiltroHasta) docsNC = docsNC.filter(n => n.fecha <= ncFiltroHasta);

  const tieneFiltrosNC = ncFiltroEstado || ncFiltroCliente || ncFiltroDesde || ncFiltroHasta;

  const filtrosUI = ncs.length > 0 ? `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:10px;display:flex;gap:10px;flex-wrap:wrap;align-items:center;">
      <select style="${inputStyle}width:160px;cursor:pointer;" onchange="state.ncFiltroEstado=this.value;render();">
        <option value="">Todos los estados</option>
        ${Object.entries(ESTADOS.NC||{}).map(([k,v])=>`<option value="${k}" ${ncFiltroEstado===k?"selected":""}>${v}</option>`).join("")}
      </select>
      <select style="${inputStyle}width:200px;cursor:pointer;" onchange="state.ncFiltroCliente=this.value;render();">
        <option value="">Todos los clientes</option>
        ${clientesNC.map(c=>`<option value="${c.id}" ${ncFiltroCliente===c.id?"selected":""}>${escapeHTML(c.nombre)}</option>`).join("")}
      </select>
      <div>
        <label style="font-size:9px;color:${C.muted};display:block;">Desde</label>
        <input type="date" value="${ncFiltroDesde}" style="${inputStyle}width:130px;" onchange="state.ncFiltroDesde=this.value;render();"/>
      </div>
      <div>
        <label style="font-size:9px;color:${C.muted};display:block;">Hasta</label>
        <input type="date" value="${ncFiltroHasta}" style="${inputStyle}width:130px;" onchange="state.ncFiltroHasta=this.value;render();"/>
      </div>
      ${tieneFiltrosNC ? `<button data-action="limpiar-filtros-nc" style="${btnStyle(null,"secondary")}">✕ Limpiar</button>` : ""}
      <div style="margin-left:auto;font-size:11.5px;color:${C.muted};">${docsNC.length} de ${ncs.length}</div>
    </div>
  ` : "";

  const rows = docsNC.length === 0
    ? `<tr><td colspan="7" style="text-align:center;padding:32px;color:${C.muted};font-size:12px;">
        ${ncs.length === 0 ? "No hay notas crédito aún. Generá una desde Facturas → 🧾 N.Crédito" : "No hay resultados con los filtros aplicados."}
       </td></tr>`
    : [...docsNC].sort((a,b)=>new Date(b.fecha)-new Date(a.fecha)).map(nc => {
        const cli = state.data.clientes.find(c=>c.id===nc.cliente_id)||{};
        const estadoLabel = (ESTADOS.NC||{})[nc.estado] || nc.estado;
        const estadoColor = ESTADO_COLOR[nc.estado] || "#6b7280";
        return `
          <tr style="border-bottom:1px solid ${C.border};">
            <td style="padding:6px 10px;font-weight:700;color:#dc2626;font-size:12px;">${escapeHTML(nc.numero)}</td>
            <td style="padding:6px 10px;font-size:11.5px;color:${C.muted};">${fmtDate(nc.fecha)}</td>
            <td style="padding:6px 10px;font-size:12px;font-weight:600;">${escapeHTML(cli.nombre||"—")}</td>
            <td style="padding:6px 10px;font-size:11.5px;">
              ${nc._origen ? `<span data-action="ir-a-documento" data-tipo="FACT" data-id="${nc._origen.id}" style="color:#059669;font-weight:700;cursor:pointer;text-decoration:underline;">${escapeHTML(nc._origen.numero)}</span>` : "—"}
            </td>
            <td style="padding:6px 10px;font-weight:700;color:#dc2626;text-align:right;">${fmtCOP(nc.total)}</td>
            <td style="padding:6px 10px;text-align:center;">
              <span style="background:${estadoColor}18;color:${estadoColor};border:1px solid ${estadoColor}40;padding:3px 9px;border-radius:20px;font-size:10.5px;font-weight:700;white-space:nowrap;">${estadoLabel}</span>
            </td>
            <td style="padding:6px 10px;text-align:right;">
              <div style="display:flex;gap:6px;justify-content:flex-end;flex-wrap:wrap;">
                <button data-action="print-nota-credito" data-id="${nc.id}" style="${btnStyle(C.mathiz,null,true)}" title="Ver la nota crédito y descargar el PDF">📄</button>
                ${nc.estado !== "anulada" ? `<button data-action="anular-nota-credito" data-id="${nc.id}" style="${btnStyle(C.red,null,true)}" title="Anular nota crédito">❌</button>` : ""}
              </div>
            </td>
          </tr>
        `;
      }).join("");

  return `
    <div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:9px;">
        <div>
          <h2 style="margin:0;font-size:17px;font-weight:800;color:#dc2626;">🧾 Notas Crédito</h2>
          <div style="font-size:11.5px;color:${C.muted};margin-top:2px;">Devoluciones, anulaciones y ajustes a facturas · MTHC-013 · Integradas con Dataico</div>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:9px;margin-bottom:10px;">
        <div style="background:white;border:2px solid #dc2626;border-radius:6px;padding:11px;">
          <div style="font-size:10.5px;color:${C.muted};font-weight:600;margin-bottom:4px;">TOTAL NOTAS CRÉDITO</div>
          <div style="font-size:16px;font-weight:800;color:#dc2626;">${fmtCOP(totalNC)}</div>
          <div style="font-size:10.5px;color:${C.muted};margin-top:4px;">${ncs.filter(n=>n.estado!=="anulada").length} vigente${ncs.filter(n=>n.estado!=="anulada").length!==1?"s":""}</div>
        </div>
      </div>

      ${filtrosUI}

      <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:hidden;">
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#f8fafc;border-bottom:2px solid #dc262620;">
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;color:${C.muted};font-weight:700;">N° NOTA</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;color:${C.muted};font-weight:700;">FECHA</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;color:${C.muted};font-weight:700;">CLIENTE</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;color:${C.muted};font-weight:700;">FACTURA</th>
              <th style="padding:6px 10px;text-align:right;font-size:10.5px;color:${C.muted};font-weight:700;">TOTAL</th>
              <th style="padding:6px 10px;text-align:center;font-size:10.5px;color:${C.muted};font-weight:700;">ESTADO</th>
              <th style="padding:6px 10px;text-align:right;font-size:10.5px;color:${C.muted};font-weight:700;">ACCIONES</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>
  `;
}

// Guarda la NC ya resuelta, genera su asiento, marca la factura como anulada
// si el crédito la cubre, y cierra el modal. dataicoMeta define si es real o
// simulada. numero = el consecutivo (Aura para local, prefijo+n° para real).
async function finalizarNotaCredito(f, ncState, numero, itemsNC, totals, dataicoMeta) {
  const { subtotal, iva, total } = totals;
  const nuevaNC = {
    id: genId(),
    numero,
    fecha: dataicoMeta.real ? todayCO() : today(),
    cliente_id: f.cliente_id,
    motivo: ncState.motivo,
    reason: ncState.reason,
    items: itemsNC,
    subtotal, iva, total,
    estado: "emitida",
    _origen: { tipo:"FACT", id: f.id, numero: f.numero, fecha: f.fecha },
    _dataico: dataicoMeta
  };
  saveData("notas_credito", [...(state.data.notas_credito||[]), nuevaNC]);

  // Asiento contable de la NC (reversa ingresos/IVA/costo y repone inventario);
  // sin esto la NC no afectaba contabilidad ni la exógena 1007.
  try {
    const astNC = await generarAsientoNotaCredito(nuevaNC);
    nuevaNC._asientoNC = astNC.id;
    saveData("notas_credito", (state.data.notas_credito||[]).map(x => x.id === nuevaNC.id ? nuevaNC : x));
  } catch (e2) {
    console.error("❌ Error generando asiento de la nota crédito:", e2);
    alert("⚠️ La NC se guardó, pero no se pudo registrar su asiento contable: " + e2.message);
  }

  // Si el total acreditado (incluyendo esta NC) cubre el total de la factura, marcarla como anulada
  const totalAcreditado = getTotalNC(f.id) + total;
  if (totalAcreditado >= parseNumber(f.total) - 0.01 && f.estado !== "pagada") {
    saveData("facturas", state.data.facturas.map(x => x.id===f.id ? {...x, estado:"anulada"} : x));
  }

  state.editingNC = null;
  state.modal = null;
  render();
  return nuevaNC;
}

async function generarNotaCreditoLocal(f, ncState, itemsNC, totals) {
  let numero;
  try {
    numero = await siguienteNumeroDoc("NC");
  } catch (err) {
    alert("⚠️ " + err.message);
    return;
  }
  const cude = "CUDE-" + Math.random().toString(36).slice(2,18).toUpperCase();
  await finalizarNotaCredito(f, ncState, numero, itemsNC, totals, {
    real: false, numero, cude, ambiente: "local", fecha: new Date().toISOString()
  });
  alert(`✅ Nota crédito ${numero} generada (local, sin validez fiscal) por ${fmtCOP(totals.total)}`);
}

async function emitirNotaCreditoDataico(f, ncState, itemsNC, totals) {
  const prefijo = (document.getElementById("nc-prefijo")?.value || "").trim();
  const numero = parseInt(document.getElementById("nc-numero")?.value, 10);
  if (!prefijo) { alert("⚠️ Escribe el prefijo de la numeración de NC (el de tu resolución en Dataico)."); return; }
  if (!numero || numero < 1) { alert("⚠️ Escribe el número consecutivo de esta nota crédito."); return; }

  const uuid = f._dataico.uuid || "";
  const cufe = f._dataico.cufe || "";

  const credit_note = {
    number: String(numero),
    issue_date: fechaDataico(todayCO()),
    reason: ncState.reason,
    numbering: { prefix: prefijo, flexible: true },
    // La misma puerta que la factura: la tarifa de IVA de CADA línea, con el
    // régimen que heredó de la factura. Antes iba un 19% quemado.
    items: itemsNC.map(itemDataico)
  };
  // Referenciar la factura original: por UUID interno de Dataico (preferido) o por CUFE.
  if (uuid) credit_note.invoice_id = uuid;
  else { credit_note.invoice_cufe = cufe; credit_note.invoice_number = f.numero; credit_note.invoice_issue_date = fechaDataico(f.fecha); }

  const cli = state.data.clientes.find(c => c.id === f.cliente_id) || {};
  const ok = confirm(`⚡ Vas a emitir la nota crédito ${prefijo}${numero} sobre la factura ${f.numero} por ${fmtCOP(totals.total)} a nombre de ${cli.nombre || ""}.\n\nSe transmite de verdad a la DIAN y anula (total o parcialmente) la factura. ¿Continuar?`);
  if (!ok) return;

  state.modal = `
    <div style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;">
      <div style="background:white;border-radius:14px;padding:30px 40px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="font-size:40px;margin-bottom:10px;">📡</div>
        <div style="font-weight:700;color:${C.text};">Transmitiendo la nota crédito a la DIAN…</div>
        <div style="font-size:11px;color:${C.muted};margin-top:6px;">No cierres esta ventana.</div>
      </div>
    </div>`;
  render();

  let resp;
  try {
    const { data, error } = await supa.functions.invoke("dataico-proxy", {
      body: { action: "nota_credito", credit_note }
    });
    if (error) throw new Error(error.message || "No se pudo contactar el servidor de facturación.");
    resp = data;
  } catch (err) {
    mostrarErrorNC(["No se pudo transmitir la nota crédito: " + (err.message || err), "La NC NO fue emitida. Revisa tu conexión e intenta de nuevo."]);
    return;
  }

  const dd = resp?.data || {};
  const httpOk = resp?.status >= 200 && resp?.status < 300;
  const doc = dd.credit_note || dd;
  const errores = Array.isArray(dd.errors) ? dd.errors : null;
  if (!httpOk || errores) {
    const lista = errores
      ? errores.map(er => (er.path ? `[${[].concat(er.path).join(".")}] ` : "") + (er.error || JSON.stringify(er)))
      : ["Dataico respondió HTTP " + resp?.status + ": " + JSON.stringify(dd).slice(0, 400)];
    rechazoNCEnBitacora(f, `${prefijo}${numero}`, totals.total, lista);
    mostrarErrorNC(["La DIAN/Dataico rechazó la nota crédito. NO fue emitida:", ...lista]);
    return;
  }
  const dianStatus = String(doc.dian_status || "").toUpperCase();
  const mensajes = Array.isArray(doc.dian_messages) ? doc.dian_messages.map(String) : [];
  const soloRechazos = mensajes.filter(m => /rechazo/i.test(m));
  const aceptada = dianStatus === "DIAN_ACEPTADO" || dianStatus === "ACEPTADO";
  if (dianStatus === "DIAN_RECHAZADO" || dianStatus === "RECHAZADO" || (!aceptada && soloRechazos.length > 0)) {
    rechazoNCEnBitacora(f, `${prefijo}${numero}`, totals.total, soloRechazos.length ? soloRechazos : mensajes, doc.dian_status);
    mostrarErrorNC([
      "La DIAN RECHAZÓ la nota crédito. NO tiene validez fiscal:",
      ...(soloRechazos.length ? soloRechazos : mensajes),
      `(estado DIAN: ${doc.dian_status || "desconocido"}). Corrige y reintenta con el mismo número.`
    ]);
    return;
  }

  const cude = doc.cude || doc.cufe || doc.uuid || "";
  const facturaNum = `${prefijo}${numero}`;
  const cfg = getDataicoConfig() || {};
  saveDataicoConfig({ ...cfg, nc_prefijo: prefijo, nc_proximo_numero: numero + 1 });

  // En la bitácora de la FACTURA: la nota crédito ya cuenta su propia
  // creación, y quien sigue la factura necesita ver que se reversó.
  registrarEvento("facturas", f.id, "emitir_nc_dian", { referencia: f.numero, detalle: {
    nota_credito: facturaNum, total: totals.total, cude, estado_dian: doc.dian_status || ""
  } });
  await finalizarNotaCredito(f, ncState, facturaNum, itemsNC, totals, {
    real: true, numero: facturaNum, cude, uuid: doc.uuid || "", ambiente: "PRODUCCION",
    proveedor: "Dataico", prefijo, dian_status: doc.dian_status || "",
    pdf_url: doc.pdf_url || "", qrcode: doc.qrcode || "", fecha: new Date().toISOString(), respuesta: dd
  });

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:520px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};background:#ecfdf5;">
          <h3 style="margin:0;font-size:16px;color:#065f46;font-weight:700;">✅ Nota crédito emitida ante la DIAN</h3>
        </div>
        <div style="padding:14px;text-align:center;">
          <div style="font-size:42px;margin-bottom:6px;">🇨🇴</div>
          <div style="font-size:17px;font-weight:800;color:#059669;">${escapeHTML(facturaNum)}</div>
          <div style="font-size:11px;color:#047857;margin-top:4px;">Sobre ${escapeHTML(f.numero)} · ${fmtCOP(totals.total)} · transmitida a la DIAN vía Dataico</div>
          ${cude ? `<div style="font-size:10px;color:${C.muted};margin-top:8px;word-break:break-all;">CUDE: ${escapeHTML(cude)}</div>` : ""}
          <div style="margin-top:14px;"><button data-action="close-modal" style="${btnStyle("#059669")}">Cerrar</button></div>
        </div>
      </div>
    </div>`;
  render();
}

function mostrarErrorNC(lineas) {
  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:540px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};background:#fef2f2;">
          <h3 style="margin:0;font-size:16px;color:#991b1b;font-weight:700;">❌ La nota crédito no se emitió</h3>
        </div>
        <div style="padding:11px;">
          ${lineas.map((l, i) => `<div style="font-size:${i === 0 ? "12.5px;font-weight:700" : "11.5px"};color:${i === 0 ? "#991b1b" : "#7f1d1d"};margin-bottom:6px;word-break:break-word;">${escapeHTML(repararTildes(l))}</div>`).join("")}
          <div style="background:#f8fafc;border-radius:6px;padding:9px;font-size:10.5px;color:${C.muted};margin-top:6px;">Nada quedó registrado en Aura ni en la DIAN.</div>
          <div style="display:flex;justify-content:flex-end;margin-top:10px;">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Entendido</button>
          </div>
        </div>
      </div>
    </div>`;
  render();
}

import { registerActions } from "../ui/actions.js";

registerActions({
  "limpiar-filtros-nc": (d, el, e) => {state.ncFiltroEstado = "";
      state.ncFiltroCliente = "";
      state.ncFiltroDesde = "";
      state.ncFiltroHasta = "";
      render();
      return;},
  "nc-anular-todo": (d, el, e) => {{
      if (!state.editingNC) return;
      state.editingNC.items.forEach(it => { it.cantidad = it.cantidad_factura; });
      if (!state.editingNC.reason) { state.editingNC.reason = "ANULACION"; state.editingNC.motivo = "Anulación de la factura"; }
      renderNotaCreditoModal();
      return;
    }},
  "confirmar-nota-credito": async (d, el, e) => {{
      const ncState = state.editingNC;
      if (!ncState) return;
      const f = (state.data.facturas||[]).find(x=>x.id===ncState.facturaId);
      if (!f) return;

      if (!ncState.motivo || !ncState.reason) { alert("⚠️ Selecciona un motivo para la nota crédito"); return; }

      const itemsNC = ncState.items
        .filter(it => parseNumber(it.cantidad) > 0)
        .map(it => ({
          articulo: it.articulo, descripcion: it.descripcion, referencia: it.referencia,
          cantidad: parseNumber(it.cantidad), precio_unitario: parseNumber(it.precio_unitario),
          total: parseNumber(it.cantidad) * parseNumber(it.precio_unitario),
          iva_tipo: it.iva_tipo || null, iva_pct: it.iva_pct ?? null
        }));

      if (itemsNC.length === 0) { alert("⚠️ Indica al menos una cantidad a acreditar"); return; }

      const subtotal = itemsNC.reduce((s,it)=>s+it.total,0);
      const iva = desgloseIVA(itemsNC).iva;
      const total = subtotal + iva;

      if (facturaEsElectronicaReal(f)) {
        await emitirNotaCreditoDataico(f, ncState, itemsNC, { subtotal, iva, total });
      } else {
        await generarNotaCreditoLocal(f, ncState, itemsNC, { subtotal, iva, total });
      }
      return;
    }},
  "print-nota-credito": (d, el, e) => {exportPDFNotaCredito(d.id);
      return;},
  "anular-nota-credito": async (d, el, e) => {{
      const ncObj = (state.data.notas_credito||[]).find(x=>x.id===d.id);
      if (!ncObj) return;
      if (!confirm(`¿Anular la nota crédito ${ncObj.numero}? Esto restaurará el saldo en la factura asociada.`)) return;
      // Reversa contable del asiento de la NC (si existía) + retiro del inventario que la NC repuso
      let ncAnulada = { ...ncObj, estado:"anulada" };
      if (ncObj._asientoNC) {
        const astNCRev = (state.data.asientos||[]).find(a => a.id === ncObj._asientoNC);
        if (astNCRev) {
          try {
            const reversaNC = await crearAsiento(
              `Anulación NC ${ncObj.numero} (reversa de ${astNCRev.numero})`,
              { tipo:"NC", id: ncObj.id, numero: ncObj.numero },
              (astNCRev.movimientos||[]).map(m => ({ cuenta: m.cuenta, debito: m.credito, credito: m.debito, tercero_id: m.tercero_id })),
              today()
            );
            ncAnulada._asientoAnulacion = reversaNC.id;
            (ncObj.items||[]).forEach(it => {
              const cant = parseNumber(it.cantidad)||0;
              if (cant) ajustarInventario(it.articulo, -cant, { tipo:"NC", id: ncObj.id, numero: ncObj.numero + " (anulación)" });
            });
          } catch (err) {
            alert("⚠️ La NC se anulará, pero no se pudo registrar la reversa contable: " + err.message);
          }
        }
      }
      saveData("notas_credito", state.data.notas_credito.map(x => x.id===d.id ? ncAnulada : x));
      // Si la factura había sido marcada como "anulada" por esta NC, revisar si debe volver a "emitida"
      if (ncObj._origen && ncObj._origen.tipo === "FACT") {
        const fObj = (state.data.facturas||[]).find(x=>x.id===ncObj._origen.id);
        if (fObj && fObj.estado === "anulada") {
          const hoy = new Date(); hoy.setHours(0,0,0,0);
          const venc = fObj.fecha_vencimiento ? new Date(fObj.fecha_vencimiento) : null;
          const nuevoEstado = (venc && venc < hoy) ? "vencida" : "emitida";
          saveData("facturas", state.data.facturas.map(x => x.id===fObj.id ? {...x, estado: nuevoEstado} : x));
        }
      }
      render();
      return;
    }},
});

function rechazoNCEnBitacora(f, numero, total, motivos, estadoDian) {
  registrarEvento("facturas", f.id, "rechazo_nc_dian", { referencia: f.numero, detalle: {
    nota_credito: numero, total, motivo: repararTildes((motivos || []).map(String).join(" | ")).slice(0, 1500), estado_dian: estadoDian || ""
  } });
}
